/**
 * client/render/shadows.ts — sun shadow maps. OWNER: RENDER agent.
 *
 * The engine renders a sun shadow map each frame for surfaces whose primary light is the sun
 * (meta.lighting.sun.note). Here:
 *  - STATIC map: every opaque/alpha-tested world surface (+ wpc/shadowcaster tool surfaces), rendered ONCE
 *    after load (nothing static moves), orthographic along the sun covering the playable yacht.
 *  - DYNAMIC map: same projection, only players (and other movers) — re-rendered every frame; cheap
 *    (one draw per player via merged depth proxies).
 *  Receivers take min(static, dynamic). Resolution ~1.5 in/texel over the yacht (ESTIMATE vs. the engine's
 *  cascaded maps; fine enough for the deck railings and awnings).
 */
import * as THREE from 'three';
import type { Bo2Uniforms } from './glsl';

function makeTarget(w: number, h: number): THREE.WebGLRenderTarget {
  const depth = new THREE.DepthTexture(w, h, THREE.UnsignedIntType);
  depth.compareFunction = THREE.LessEqualCompare;
  depth.minFilter = depth.magFilter = THREE.LinearFilter;
  const rt = new THREE.WebGLRenderTarget(w, h, { depthTexture: depth, depthBuffer: true, format: THREE.RedFormat, type: THREE.UnsignedByteType });
  rt.texture.generateMipmaps = false;
  return rt;
}

export class SunShadows {
  readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 2);
  readonly staticScene = new THREE.Scene();
  readonly dynamicScene = new THREE.Scene();
  private staticRT: THREE.WebGLRenderTarget | null = null;
  private dynRT: THREE.WebGLRenderTarget | null = null;
  private staticDirty = true;
  private targetsReady = false;
  readonly matrix = new THREE.Matrix4();
  enabled = true;
  dynamicEnabled = true;

  constructor(private readonly U: Bo2Uniforms) {
    this.staticScene.matrixWorldAutoUpdate = false;
  }

  /**
   * Fit the orthographic sun camera around `box` (three space) and allocate the maps.
   * `texelSize` in inches.
   */
  setup(box: THREE.Box3, sunDirThree: THREE.Vector3, texelSize: number, maxSize: number): void {
    const center = box.getCenter(new THREE.Vector3());
    const radius = box.getSize(new THREE.Vector3()).length() / 2;
    const cam = this.camera;
    cam.position.copy(center).addScaledVector(sunDirThree, radius + 2000);
    cam.up.set(0, 1, 0);
    if (Math.abs(sunDirThree.y) > 0.99) cam.up.set(1, 0, 0);
    cam.lookAt(center);
    cam.updateMatrixWorld(true);
    const inv = cam.matrixWorldInverse;
    const mn = new THREE.Vector3(Infinity, Infinity, Infinity), mx = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    const p = new THREE.Vector3();
    for (let i = 0; i < 8; i++) {
      p.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).applyMatrix4(inv);
      mn.min(p); mx.max(p);
    }
    cam.left = mn.x; cam.right = mx.x; cam.bottom = mn.y; cam.top = mx.y;
    cam.near = Math.max(1, -mx.z - 4000); // extra depth toward the sun for tall casters (mast, superstructure)
    cam.far = -mn.z + 50;
    cam.updateProjectionMatrix();
    const w = Math.min(maxSize, THREE.MathUtils.ceilPowerOfTwo(Math.ceil((mx.x - mn.x) / texelSize)));
    const h = Math.min(maxSize, THREE.MathUtils.ceilPowerOfTwo(Math.ceil((mx.y - mn.y) / texelSize)));
    this.staticRT?.dispose();
    this.dynRT?.dispose();
    this.staticRT = makeTarget(w, h);
    this.dynRT = makeTarget(w, h);
    const bias = new THREE.Matrix4().set(0.5, 0, 0, 0.5, 0, 0.5, 0, 0.5, 0, 0, 0.5, 0.5, 0, 0, 0, 1);
    this.matrix.multiplyMatrices(bias, cam.projectionMatrix).multiply(cam.matrixWorldInverse);
    this.U.uShadowMatrix.value.copy(this.matrix);
    this.U.uShadowStatic.value = this.staticRT.depthTexture;
    this.U.uShadowDyn.value = this.dynRT.depthTexture;
    this.U.uShadowParams.value.set(1 / w, 1 / h, this.enabled ? 1 : 0, 0);
    this.staticDirty = true;
    this.targetsReady = false;
  }

  addStaticCasters(meshes: THREE.Mesh[]): void {
    for (const m of meshes) {
      const dm = m.userData.depthMaterial as THREE.Material | null;
      if (!dm) continue;
      const proxy = new THREE.Mesh(m.geometry, dm);
      proxy.matrixAutoUpdate = false;
      proxy.frustumCulled = false;
      this.staticScene.add(proxy);
    }
    this.staticDirty = true;
  }

  /** Drop every static caster (GPU context restore rebuilds the world and re-adds them). */
  clearStaticCasters(): void {
    // the old targets belong to the lost context: drop them without dispose() (deleting foreign GL objects
    // only produces INVALID_OPERATION warnings); setup() allocates new ones
    this.staticRT = null;
    this.dynRT = null;
    this.staticScene.clear();
    this.staticDirty = true;
    this.targetsReady = false;
  }

  get size(): [number, number] { return this.staticRT ? [this.staticRT.width, this.staticRT.height] : [0, 0]; }

  /** Returns the number of draw calls issued. */
  render(r: THREE.WebGLRenderer, dynamicVisible: number): number {
    if (!this.staticRT || !this.dynRT || !this.enabled) { this.U.uShadowParams.value.z = 0; return 0; }
    this.U.uShadowParams.value.z = 1;
    let calls = 0;
    const prevTarget = r.getRenderTarget();
    if (!this.targetsReady) {
      // Create both framebuffers (and their depth textures, with compare mode) now: a DepthTexture that was
      // never attached has no GL texture yet, and binding it to a sampler2DShadow fails every draw.
      for (const rt of [this.staticRT, this.dynRT]) { r.setRenderTarget(rt); r.clear(true, true, false); }
      this.targetsReady = true;
    }
    if (this.staticDirty) {
      r.setRenderTarget(this.staticRT);
      r.clear(true, true, false);
      const before = r.info.render.calls;
      r.render(this.staticScene, this.camera);
      calls += r.info.render.calls - before;
      this.staticDirty = false;
    }
    const dyn = this.dynamicEnabled && dynamicVisible > 0;
    this.U.uShadowParams.value.w = dyn ? 1 : 0;
    if (dyn) {
      r.setRenderTarget(this.dynRT);
      r.clear(true, true, false);
      const before = r.info.render.calls;
      r.render(this.dynamicScene, this.camera);
      calls += r.info.render.calls - before;
    }
    r.setRenderTarget(prevTarget);
    return calls;
  }

  invalidate(): void { this.staticDirty = true; }

  dispose(): void {
    this.staticRT?.dispose();
    this.dynRT?.dispose();
  }
}
